/**
 * Ultra-Modern Users Page
 * Row Click → Opens Dedicated User Profile Page
 * Smart Sliding Pagination + Rows per page + Page info
 * Top 3 buttons ENABLED as requested
 */
import React, { useState, useRef, useEffect, useLayoutEffect, type CSSProperties } from 'react';
import { createPortal } from 'react-dom';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { DataTable } from '../../components/shared/DataTable';
import { Modal } from '../../components/shared/Modal';
import { UserForm } from './UserForm';
import { useToast } from '../../components/shared/Toast';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  MdCheckCircle,
  MdCancel,
  MdFilterList,
  MdPeople,
  MdSearch,
  MdEmail,
  MdUpload,
  MdChevronLeft,
  MdChevronRight,
  MdInfo,
  MdDownload,
  MdMoreVert,
  MdEdit,
  MdDelete,
} from 'react-icons/md';
import { useDebouncedSearch } from '@euroasiann/shared';
import { ClearFiltersButton, PageSkeleton } from '@euroasiann/shared/ui';
import { cn } from '../../lib/utils';
import { triggerBlobDownload } from '../../utils/download';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000';

const BULK_UPLOAD_COLUMNS: { key: string; required: boolean; description: string }[] = [
  { key: 'email', required: true, description: 'Work email address (must be unique)' },
  { key: 'firstName', required: true, description: 'Given name' },
  { key: 'lastName', required: true, description: 'Family name' },
  { key: 'roleName', required: true, description: 'Existing role name, for example Super Admin' },
  { key: 'portalType', required: true, description: 'admin, tech, customer, or vendor' },
  { key: 'isActive', required: false, description: 'true or false; defaults to true if omitted' },
  { key: 'employeeId', required: false, description: 'Staff identifier, for example EMP-000001' },
  { key: 'phone', required: false, description: 'Contact number with country code' },
  { key: 'department', required: false, description: 'Department name' },
  { key: 'designation', required: false, description: 'Job title' },
  { key: 'location', required: false, description: 'Office or site' },
  { key: 'reportingTo', required: false, description: 'Manager email or name' },
];

const BULK_UPLOAD_SAMPLE_ROWS = [
  {
    email: 'john.doe@example.com',
    firstName: 'John',
    lastName: 'Doe',
    roleName: 'Tech Lead',
    portalType: 'tech',
    isActive: 'true',
    employeeId: 'EA-TECH-001',
    phone: '+91 98765 43210',
    department: 'Technology',
    designation: 'Senior DevOps Engineer',
    location: 'Delhi HQ',
    reportingTo: 'manager@example.com',
  },
  {
    email: 'jane.smith@example.com',
    firstName: 'Jane',
    lastName: 'Smith',
    roleName: 'Developer',
    portalType: 'tech',
    isActive: 'true',
    employeeId: 'EA-TECH-002',
    phone: '+91 98765 43211',
    department: 'Technology',
    designation: 'Frontend Developer',
    location: 'Delhi HQ',
    reportingTo: 'john.doe@example.com',
  },
] as const;

const BULK_UPLOAD_HEADERS = BULK_UPLOAD_COLUMNS.map((column) => column.key);

interface User {
  _id: string;
  email: string;
  firstName: string;
  lastName: string;
  fullName?: string;
  portalType: string;
  role: string;
  roleId?: string | { _id: string; name: string; key: string; permissions?: string[] };
  roleName?: string;
  isActive: boolean;
  organizationId?: string;
  lastLogin?: string;
  createdAt?: string;
  phone?: string;
  employeeId?: string;
  department?: string;
  designation?: string;
  location?: string;
  reportingManager?: string;
}

function UserRowActions({
  user,
  onEdit,
  onDelete,
}: {
  user: User;
  onEdit: (user: User) => void;
  onDelete: (user: User) => void;
}) {
  const [open, setOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [menuStyle, setMenuStyle] = useState<CSSProperties>({});

  const updateMenuPosition = () => {
    const btn = buttonRef.current;
    if (!btn) return;
    const rect = btn.getBoundingClientRect();
    const width = 160;
    let left = rect.right - width;
    if (left < 8) left = 8;
    const spaceBelow = window.innerHeight - rect.bottom;
    const openUp = spaceBelow < 120;
    setMenuStyle({
      position: 'fixed',
      zIndex: 2300,
      top: openUp ? undefined : rect.bottom + 4,
      bottom: openUp ? window.innerHeight - rect.top + 4 : undefined,
      left,
      width,
    });
  };

  useLayoutEffect(() => {
    if (!open) return;
    updateMenuPosition();
    const onMove = () => updateMenuPosition();
    window.addEventListener('resize', onMove);
    window.addEventListener('scroll', onMove, true);
    return () => {
      window.removeEventListener('resize', onMove);
      window.removeEventListener('scroll', onMove, true);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      const t = e.target as Node;
      if (buttonRef.current?.contains(t) || menuRef.current?.contains(t)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div className="flex justify-center" onClick={(e) => e.stopPropagation()}>
      <button
        ref={buttonRef}
        type="button"
        aria-label="User actions"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className="p-2 rounded-lg text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] hover:bg-[hsl(var(--muted))] transition-colors"
      >
        <MdMoreVert className="w-5 h-5" />
      </button>
      {open
        ? createPortal(
            <div
              ref={menuRef}
              role="menu"
              style={menuStyle}
              className="rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] py-1 shadow-lg"
            >
              <button
                type="button"
                role="menuitem"
                className="flex w-full items-center gap-2 px-3 py-2 text-sm text-[hsl(var(--foreground))] hover:bg-[hsl(var(--muted))]"
                onClick={() => {
                  setOpen(false);
                  onEdit(user);
                }}
              >
                <MdEdit className="h-4 w-4" /> Edit
              </button>
              <button
                type="button"
                role="menuitem"
                className="flex w-full items-center gap-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50 dark:hover:bg-red-950/30"
                onClick={() => {
                  setOpen(false);
                  onDelete(user);
                }}
              >
                <MdDelete className="h-4 w-4" /> Delete
              </button>
            </div>,
            document.body
          )
        : null}
    </div>
  );
}

export function UsersPage() {
  const queryClient = useQueryClient();
  const toast = useToast();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSampleDataModalOpen, setIsSampleDataModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  
  // Initialize state from URL params
  const [filterActive, setFilterActive] = useState<string>(searchParams.get('filter') || 'all');
  const [page, setPage] = useState(parseInt(searchParams.get('page') || '1', 10));
  const [rowsPerPage, setRowsPerPage] = useState(parseInt(searchParams.get('limit') || '10', 10));
  const {
    inputValue: searchTerm,
    submittedValue: submittedSearch,
    setInputValue: setSearchTerm,
    clear: clearSearch,
    onKeyDown: onSearchKeyDown,
  } = useDebouncedSearch(searchParams.get('search') || '', {
    onCommit: () => setPage(1),
  });

  const fileInputRef = useRef<HTMLInputElement | null>(null);

  // Update URL params when state changes
  useEffect(() => {
    const params = new URLSearchParams();
    if (filterActive !== 'all') params.set('filter', filterActive);
    if (submittedSearch) params.set('search', submittedSearch);
    if (page > 1) params.set('page', page.toString());
    if (rowsPerPage !== 10) params.set('limit', rowsPerPage.toString());
    
    setSearchParams(params, { replace: true });
  }, [filterActive, submittedSearch, page, rowsPerPage, setSearchParams]);

  const { data: usersResponse, isLoading, error: queryError } = useQuery<{
    data: User[];
    pagination: {
      total: number;
      filteredTotal: number;
      page: number;
      limit: number;
      totalPages: number;
      activeCount: number;
      inactiveCount: number;
    };
  }>({
    queryKey: ['admin-users', filterActive, page, rowsPerPage, submittedSearch],
    queryFn: async ({ signal }) => {
      const params = new URLSearchParams();
      if (filterActive !== 'all') {
        params.append('isActive', filterActive === 'active' ? 'true' : 'false');
      }
      if (submittedSearch) {
        params.append('search', submittedSearch);
      }
      params.append('page', page.toString());
      params.append('limit', rowsPerPage.toString());
      const response = await fetch(`${API_URL}/api/v1/admin/users?${params}`, {
        signal,
        headers: {
          Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
        },
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to fetch users');
      }
      const data = await response.json();
      return data;
    },
  });

  const usersData = usersResponse?.data || [];
  const totalUsers = usersResponse?.pagination?.total || 0;
  const filteredTotal = usersResponse?.pagination?.filteredTotal ?? usersResponse?.pagination?.total ?? 0;
  const totalActiveUsers = usersResponse?.pagination?.activeCount ?? 0;
  const totalInactiveUsers = usersResponse?.pagination?.inactiveCount ?? 0;

  const deleteMutation = useMutation({
    mutationFn: async (userId: string) => {
      const response = await fetch(`${API_URL}/api/v1/admin/users/${userId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
        },
        body: JSON.stringify({ _id: userId }),
      });
      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.error || 'Failed to delete user');
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-users'] });
      toast.success('User deleted successfully!');
    },
    onError: (error: Error) => {
      toast.error(`Failed to delete user: ${error.message}`);
    },
  });

  const bulkUploadMutation = useMutation({
    mutationFn: async (file: File) => {
      const formData = new FormData();
      formData.append('file', file);
      const response = await fetch(`${API_URL}/api/v1/admin/users/bulk-upload`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
        },
        body: formData,
      });
      
      const responseData = await response.json();
      
      // Log full response for debugging
      console.log('📤 Bulk Upload Response:', responseData);
      
      if (!response.ok) {
        const errorMsg = responseData.error || responseData.message || 'Bulk upload failed';
        console.error('❌ Bulk Upload Error:', errorMsg, responseData);
        throw new Error(errorMsg);
      }
      
      return responseData;
    },
    onSuccess: (data) => {
      const responseData = data.data || data;
      const created = responseData?.created || 0;
      const updated = responseData?.updated || 0;
      const skipped = responseData?.skipped || 0;
      const errors = responseData?.details?.errors || [];
      const skippedItems = responseData?.details?.skipped || [];
      const createdItems = responseData?.details?.created || [];
      const updatedItems = responseData?.details?.updated || [];
      
      // Log detailed information
      console.log('📊 Bulk Upload Results:', {
        created: { count: created, items: createdItems },
        updated: { count: updated, items: updatedItems },
        skipped: { count: skipped, items: skippedItems },
        errors: errors,
      });
      
      // Build success message
      let message = `Bulk upload completed!`;
      const parts: string[] = [];
      if (created > 0) parts.push(`Created: ${created}`);
      if (updated > 0) parts.push(`Updated: ${updated}`);
      if (skipped > 0) parts.push(`Skipped: ${skipped}`);
      
      if (parts.length > 0) {
        message += ` ${parts.join(', ')}`;
        toast.success(message);
      } else {
        toast.success('Bulk upload completed!');
      }
      
      queryClient.invalidateQueries({ queryKey: ['admin-users'] });
      
      // Show detailed error messages if there are errors
      if (errors?.length > 0) {
        console.error('❌ Bulk Upload Errors:', errors);
        console.error('❌ Skipped Items:', skippedItems);
        
        // Show first few errors in a toast
        const errorPreview = errors.slice(0, 3);
        const remainingErrors = errors.length - 3;
        
        let errorMessage = `Found ${errors.length} error(s): ${errorPreview.join(' | ')}`;
        if (remainingErrors > 0) {
          errorMessage += ` ... and ${remainingErrors} more. Check console (F12) for full details.`;
        } else {
          errorMessage += ' Check console (F12) for details.';
        }
        
        // Show error toast with details
        setTimeout(() => {
          toast.error(errorMessage);
        }, 500);
        
        // Also show skipped items summary
        if (skippedItems.length > 0) {
          setTimeout(() => {
            const skippedMsg = skippedItems.length <= 10 
              ? `Skipped: ${skippedItems.join(', ')}` 
              : `Skipped ${skippedItems.length} items. First 10: ${skippedItems.slice(0, 10).join(', ')}. Check console (F12) for full list.`;
            toast.warning(skippedMsg);
          }, 1500);
        }
      } else if (skipped > 0) {
        // If there are skipped items but no errors, show info about skipped items
        console.warn('⚠️ Skipped items (no errors):', skippedItems);
        setTimeout(() => {
          const skippedMsg = skippedItems.length <= 10 
            ? `Skipped ${skippedItems.length} items (no changes or duplicates): ${skippedItems.join(', ')}` 
            : `Skipped ${skippedItems.length} items (no changes or duplicates). Check console (F12) for full list.`;
          toast.warning(skippedMsg);
        }, 500);
      }
    },
    onError: (error: Error) => {
      console.error('❌ Bulk Upload Failed:', error);
      toast.error(`Bulk upload failed: ${error.message}`);
    },
  });

  const handleBulkUploadFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const allowedTypes = [
      'text/csv',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    ];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Invalid file type. Please upload CSV or Excel.');
      return;
    }

    bulkUploadMutation.mutate(file);
    if (e.target) e.target.value = '';
  };

  const handleDownloadSample = () => {
    const csvContent = [
      BULK_UPLOAD_HEADERS.join(','),
      ...BULK_UPLOAD_SAMPLE_ROWS.map((row) =>
        BULK_UPLOAD_HEADERS.map((header) => {
          const value = row[header as keyof typeof row] || '';
          return `"${String(value).replace(/"/g, '""')}"`;
        }).join(',')
      ),
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    triggerBlobDownload(blob, 'bulk_upload_sample.csv', '.csv');
    toast.success('Sample CSV downloaded!');
  };

  const handleInvite = () => {
    setEditingUser(null);
    setIsModalOpen(true);
  };

  const handleEdit = async (user: User) => {
    setEditingUser(user);
    setIsModalOpen(true);
    try {
      const response = await fetch(`${API_URL}/api/v1/admin/users/${user._id}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
        },
      });
      if (!response.ok) return;
      const json = await response.json();
      const detail = json.data?.user || json.data;
      const profile = json.data?.profile;
      if (!detail) return;
      const reporting =
        typeof profile?.reportingTo === 'object'
          ? profile.reportingTo?.fullName || profile.reportingTo?.email || ''
          : profile?.reportingTo || '';
      setEditingUser({
        ...user,
        ...detail,
        _id: detail._id || user._id,
        phone: profile?.phone || detail.phone || '',
        employeeId: profile?.employeeId || '',
        department: profile?.department || '',
        designation: profile?.designation || '',
        location: profile?.location || '',
        reportingManager: reporting,
      });
    } catch {
      // List row is enough to open the popup; profile fields stay empty if fetch fails.
    }
  };

  const handleDelete = (user: User) => {
    if (window.confirm(`Are you sure you want to delete user ${user.email}?`)) {
      deleteMutation.mutate(user._id);
    }
  };

  const handleClose = () => {
    setIsModalOpen(false);
    setEditingUser(null);
  };

  const handleSuccess = () => {
    queryClient.invalidateQueries({ queryKey: ['admin-users'] });
    handleClose();
    setPage(1);
  };

  const handleRowClick = (user: User) => {
    navigate(`/users/${user._id}`);
  };

  const getSafeRoleLabel = (user: User): string => {
    if (typeof user.roleId === 'object' && user.roleId?.name) return user.roleId.name;
    if (user.roleName) return user.roleName;
    if (user.role) return user.role.replace('_', ' ').replace(/\b\w/g, (l) => l.toUpperCase());
    return 'No Role';
  };

  // Use filteredTotal for pagination (when filter/search is applied)
  // Use totalUsers for stats cards (always show total counts)
  const totalPages = Math.ceil(filteredTotal / rowsPerPage);
  const startIndex = filteredTotal === 0 ? 0 : (page - 1) * rowsPerPage + 1;
  const endIndex = Math.min(page * rowsPerPage, filteredTotal);
  
  // Display users from server (search and filter are server-side)
  const paginatedUsers = usersData;

  const getVisiblePages = () => {
    const visibleCount = 5;
    let start = Math.max(1, page - Math.floor(visibleCount / 2));
    let end = start + visibleCount - 1;

    if (end > totalPages) {
      end = totalPages;
      start = Math.max(1, end - visibleCount + 1);
    }

    const pages = [];
    for (let i = start; i <= end; i++) {
      pages.push(i);
    }
    return pages;
  };

  const visiblePages = getVisiblePages();

  useEffect(() => {
    // Only adjust page if we have valid totalPages and page is out of bounds
    // Don't reset to page 1 when data is loading (totalPages === 0)
    if (totalPages > 0 && page > totalPages) {
      setPage(totalPages);
    }
    // Removed: else if (totalPages === 0) setPage(1);
    // This was causing page to reset to 1 during data loading
  }, [totalPages, page]);

  const columns = [
    {
      key: 'name',
      header: 'Name',
      render: (user: User) => (
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-[#0088cc] text-sm font-bold text-white shadow-sm">
            {user.firstName?.[0]?.toUpperCase() || user.email[0].toUpperCase()}
          </div>
          <div className="font-semibold text-[hsl(var(--foreground))]">
            {`${user.firstName || ''} ${user.lastName || ''}`.trim() || '—'}
          </div>
        </div>
      ),
    },
    {
      key: 'email',
      header: 'Email',
      render: (user: User) => (
        <div className="text-sm text-[hsl(var(--foreground))]">{user.email}</div>
      ),
    },
    {
      key: 'role',
      header: 'Role',
      render: (user: User) => (
        <span className="px-3 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800 dark:bg-blue-900/40">
          {getSafeRoleLabel(user)}
        </span>
      ),
    },
    {
      key: 'portalType',
      header: 'Portal Type',
      render: (user: User) => (
        <span className="px-3 py-1 text-xs font-semibold rounded-full bg-purple-100 text-purple-800 dark:bg-purple-900/40">
          {user.portalType.charAt(0).toUpperCase() + user.portalType.slice(1)}
        </span>
      ),
    },
    {
      key: 'isActive',
      header: 'Status',
      render: (user: User) => (
        <span
          className={cn(
            'px-3 py-1 inline-flex items-center gap-1.5 text-xs font-semibold rounded-full',
            user.isActive
              ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40'
              : 'bg-red-100 text-red-800 dark:bg-red-900/40'
          )}
        >
          {user.isActive ? (
            <>
              <MdCheckCircle className="w-3.5 h-3.5" /> Active
            </>
          ) : (
            <>
              <MdCancel className="w-3.5 h-3.5" /> Inactive
            </>
          )}
        </span>
      ),
    },
    {
      key: 'lastLogin',
      header: 'Last Login',
      render: (user: User) => (
        <div className="text-sm text-[hsl(var(--muted-foreground))]">
          {user.lastLogin ? new Date(user.lastLogin).toLocaleDateString() : 'Never'}
        </div>
      ),
    },
    {
      key: 'createdAt',
      header: 'Created At',
      render: (user: User) => (
        <div className="text-sm text-[hsl(var(--muted-foreground))]">
          {user.createdAt ? new Date(user.createdAt).toLocaleDateString() : 'N/A'}
        </div>
      ),
    },
    {
      key: 'actions',
      header: 'Actions',
      className: 'w-20 text-center',
      render: (user: User) => (
        <UserRowActions user={user} onEdit={handleEdit} onDelete={handleDelete} />
      ),
    },
  ];

  // Stats use server-side total counts
  const stats = [
    { label: 'Total Users', value: totalUsers, icon: MdPeople },
    { label: 'Active', value: totalActiveUsers, icon: MdCheckCircle },
    { label: 'Inactive', value: totalInactiveUsers, icon: MdCancel },
  ];

  return (
    <div className="w-full space-y-6">
      <input
        type="file"
        accept=".csv,.xlsx,.xls"
        ref={fileInputRef}
        onChange={handleBulkUploadFile}
        className="hidden"
      />

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-[hsl(var(--foreground))] mb-1">
            Users Management
          </h1>
          <p className="text-[hsl(var(--muted-foreground))]">
            Manage users and permissions across the platform
          </p>
        </div>

        {/* Top 3 buttons - Fully ENABLED */}
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => setIsSampleDataModalOpen(true)}
            className="flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl font-semibold shadow-sm transition-all bg-[#0088cc] text-white hover:bg-[#0077b3]"
          >
            <MdInfo className="w-5 h-5" />
            Sample Data
          </button>
          
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={bulkUploadMutation.isPending}
            className={cn(
              'flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl font-semibold shadow-sm transition-all border-2',
              bulkUploadMutation.isPending
                ? 'bg-[#0088cc]/10 dark:bg-[#0088cc]/18 text-[#0088cc]/65 border-[#0088cc]/25 dark:border-[#0088cc]/20 cursor-not-allowed'
                : 'bg-white dark:bg-slate-800 text-emerald-600 dark:text-emerald-400 border-emerald-600 dark:border-emerald-400 hover:bg-emerald-50 dark:hover:bg-emerald-950/30'
            )}
          >
            {bulkUploadMutation.isPending ? (
              <div className="w-5 h-5 border-2 border-emerald-600 dark:border-emerald-400 border-t-transparent rounded-full animate-spin" />
            ) : (
              <MdUpload className="w-5 h-5" />
            )}
            Bulk Upload
          </button>

          <button
            type="button"
            onClick={handleInvite}
            className="flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl font-semibold shadow-sm transition-all bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] hover:opacity-90"
          >
            <MdEmail className="w-5 h-5" />
            Invite Admin
          </button>
        </div>
      </div>

      {/* Rest of your component remains EXACTLY the same */}
      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {stats.map((stat, index) => {
          const Icon = stat.icon;
          return (
            <div
              key={index}
              className="p-6 rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] shadow-sm hover:shadow-md transition-shadow"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-[hsl(var(--muted-foreground))] mb-1">{stat.label}</p>
                  <p className="text-3xl font-bold text-[hsl(var(--foreground))]">{stat.value}</p>
                </div>
                <div className="w-12 h-12 rounded-xl bg-blue-50 dark:bg-blue-950/30 flex items-center justify-center">
                  <Icon className="w-6 h-6 text-[hsl(var(--primary))]" />
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Search & Filter */}
      <div className="p-4 rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] shadow-sm">
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
          <div className="relative flex-1 w-full">
            <MdSearch className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[hsl(var(--muted-foreground))]" />
            <input
              type="text"
              placeholder="Search by email, name, or role..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={onSearchKeyDown}
              className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] text-[hsl(var(--foreground))] focus:ring-2 focus:ring-[hsl(var(--primary))] focus:border-transparent transition-all"
            />
          </div>

          <div className="flex items-center gap-2">
            <MdFilterList className="w-5 h-5 text-[hsl(var(--muted-foreground))]" />
            <select
              value={filterActive}
              onChange={(e) => {
                setFilterActive(e.target.value);
                setPage(1);
              }}
              className="px-4 py-2.5 rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] text-[hsl(var(--foreground))] focus:ring-2 focus:ring-[hsl(var(--primary))] transition-all"
            >
              <option value="all">All Users</option>
              <option value="active">Active Only</option>
              <option value="inactive">Inactive Only</option>
            </select>
            <ClearFiltersButton
              visible={!!searchTerm.trim() || filterActive !== 'all'}
              onClick={() => {
                clearSearch();
                setFilterActive('all');
                setPage(1);
              }}
              variant="toolbar"
            />
          </div>
        </div>
      </div>

      {/* Table + Pagination */}
      <div className="space-y-4">
        {isLoading ? (
          <PageSkeleton variant="table" />
        ) : queryError ? (
          <div className="p-12 text-center rounded-xl border border-red-200 bg-red-50 shadow-sm">
            <p className="text-red-600 font-semibold mb-2">Error loading users</p>
            <p className="text-sm text-red-500">{(queryError as Error).message}</p>
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between text-sm text-[hsl(var(--muted-foreground))]">
              <div>
                {filteredTotal > 0
                  ? `Showing ${startIndex}–${endIndex} of ${filteredTotal} users`
                  : 'No users found.'}
              </div>
            </div>

            <DataTable
              columns={columns}
              data={paginatedUsers}
              onRowClick={handleRowClick}
              emptyMessage="No users found matching your search."
            />

            {/* Full Pagination with Rows per page + Page info */}
            <div className="flex flex-col sm:flex-row items-center justify-between gap-6 py-6">
              {/* Rows per page */}
              <div className="flex items-center gap-3 text-sm text-[hsl(var(--muted-foreground))]">
                <span>Rows per page:</span>
                <select
                  value={rowsPerPage}
                  onChange={(e) => {
                    setRowsPerPage(Number(e.target.value));
                    setPage(1);
                  }}
                  className="px-4 py-2 rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] text-[hsl(var(--foreground))] focus:outline-none focus:ring-2 focus:ring-[hsl(var(--primary))]"
                >
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                </select>
              </div>

              {/* Sliding Pagination - Only show if more than 1 page */}
              {totalPages > 1 && (
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className={cn(
                      'p-3 rounded-xl transition-all',
                      page === 1
                        ? 'text-[hsl(var(--muted-foreground))] cursor-not-allowed'
                        : 'text-[hsl(var(--foreground))] hover:bg-[hsl(var(--accent))]'
                    )}
                  >
                    <MdChevronLeft className="w-5 h-5" />
                  </button>

                  {visiblePages.map((pageNum) => (
                    <button
                      key={pageNum}
                      onClick={() => setPage(pageNum)}
                      className={cn(
                        'w-12 h-12 rounded-xl font-medium transition-all',
                        page === pageNum
                          ? 'bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))]'
                          : 'hover:bg-[hsl(var(--accent))]'
                      )}
                    >
                      {pageNum}
                    </button>
                  ))}

                  {visiblePages[visiblePages.length - 1] < totalPages && (
                    <>
                      <span className="px-3 text-[hsl(var(--muted-foreground))]">...</span>
                      <button
                        onClick={() => setPage(totalPages)}
                        className={cn(
                          'w-12 h-12 rounded-xl font-medium transition-all hover:bg-[hsl(var(--accent))]'
                        )}
                      >
                        {totalPages}
                      </button>
                    </>
                  )}

                  <button
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                    className={cn(
                      'p-3 rounded-xl transition-all',
                      page === totalPages
                        ? 'text-[hsl(var(--muted-foreground))] cursor-not-allowed'
                        : 'text-[hsl(var(--foreground))] hover:bg-[hsl(var(--accent))]'
                    )}
                  >
                    <MdChevronRight className="w-5 h-5" />
                  </button>
                </div>
              )}

              {/* Page X of Y */}
              <div className="text-sm text-[hsl(var(--muted-foreground))] font-medium">
                Page {page} of {totalPages || 1}
              </div>
            </div>
          </>
        )}
      </div>

      {/* Modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={handleClose}
        title={editingUser ? 'Edit administrator' : 'Invite administrator'}
        description={
          editingUser
            ? "Update this administrator's profile, employment details, and assigned role."
            : 'Create admin-portal access for a colleague. Temporary credentials will be emailed to the address below.'
        }
        size="medium"
        compact
      >
        <UserForm user={editingUser} onSuccess={handleSuccess} onCancel={handleClose} />
      </Modal>

      {/* Sample Data Modal */}
      <Modal
        isOpen={isSampleDataModalOpen}
        onClose={() => setIsSampleDataModalOpen(false)}
        title="Bulk upload template"
        description="Match this column layout in CSV or Excel. Header names must be identical. Accepted files: .csv, .xls, .xlsx."
        size="large"
        compact
      >
        <div className="space-y-3">
          <section className="overflow-hidden rounded-md border border-[hsl(var(--border))]">
            <div className="border-b border-[hsl(var(--border))] bg-[hsl(var(--muted))]/40 px-3 py-2">
              <h3 className="text-xs font-semibold tracking-wide text-[hsl(var(--foreground))]">Column specification</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-[hsl(var(--border))] text-left text-[11px] font-semibold uppercase tracking-wide text-[hsl(var(--muted-foreground))]">
                    <th className="px-3 py-2">Column</th>
                    <th className="px-3 py-2 w-28">Requirement</th>
                    <th className="px-3 py-2">Description</th>
                  </tr>
                </thead>
                <tbody>
                  {BULK_UPLOAD_COLUMNS.map((column) => (
                    <tr key={column.key} className="border-b border-[hsl(var(--border))] last:border-b-0">
                      <td className="px-3 py-1.5 font-mono text-xs text-[hsl(var(--foreground))]">{column.key}</td>
                      <td className="px-3 py-1.5">
                        <span
                          className={cn(
                            'inline-flex rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide',
                            column.required
                              ? 'bg-[hsl(var(--primary))]/10 text-[hsl(var(--primary))]'
                              : 'bg-[hsl(var(--muted))] text-[hsl(var(--muted-foreground))]'
                          )}
                        >
                          {column.required ? 'Required' : 'Optional'}
                        </span>
                      </td>
                      <td className="px-3 py-1.5 text-xs text-[hsl(var(--muted-foreground))]">{column.description}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          <section className="overflow-hidden rounded-md border border-[hsl(var(--border))]">
            <div className="border-b border-[hsl(var(--border))] bg-[hsl(var(--muted))]/40 px-3 py-2">
              <h3 className="text-xs font-semibold tracking-wide text-[hsl(var(--foreground))]">Example rows</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full min-w-[720px] text-xs">
                <thead>
                  <tr className="border-b border-[hsl(var(--border))] text-left font-semibold text-[hsl(var(--muted-foreground))]">
                    {['email', 'firstName', 'lastName', 'roleName', 'portalType', 'employeeId', 'department'].map((header) => (
                      <th key={header} className="whitespace-nowrap px-3 py-2 font-mono">
                        {header}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {BULK_UPLOAD_SAMPLE_ROWS.map((row) => (
                    <tr key={row.email} className="border-b border-[hsl(var(--border))] last:border-b-0 text-[hsl(var(--foreground))]">
                      <td className="whitespace-nowrap px-3 py-1.5">{row.email}</td>
                      <td className="whitespace-nowrap px-3 py-1.5">{row.firstName}</td>
                      <td className="whitespace-nowrap px-3 py-1.5">{row.lastName}</td>
                      <td className="whitespace-nowrap px-3 py-1.5">{row.roleName}</td>
                      <td className="whitespace-nowrap px-3 py-1.5">{row.portalType}</td>
                      <td className="whitespace-nowrap px-3 py-1.5">{row.employeeId}</td>
                      <td className="whitespace-nowrap px-3 py-1.5">{row.department}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          <div className="flex items-center justify-end gap-2 pt-1">
            <button
              type="button"
              onClick={() => setIsSampleDataModalOpen(false)}
              className="h-9 px-4 rounded-md border border-[hsl(var(--border))] bg-[hsl(var(--card))] text-sm font-medium text-[hsl(var(--foreground))] hover:bg-[hsl(var(--muted))] transition"
            >
              Close
            </button>
            <button
              type="button"
              onClick={handleDownloadSample}
              className="h-9 px-4 rounded-md bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] text-sm font-semibold hover:opacity-90 inline-flex items-center gap-2"
            >
              <MdDownload className="h-4 w-4" />
              Download CSV template
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}


